import * as i18n from 'i18next';
import { orderBy, uniqBy } from 'lodash-es';
import differenceBy from 'lodash-es/differenceBy';
import intersectionBy from 'lodash-es/intersectionBy';
import * as moment from 'moment';

import { carrierRebrandMap } from '~/constants/carriers';
import { currencies } from '~/constants/currency';
import { type BatchEntryExtended } from '~/types/OrderBatchEntry';
import ServiceLevel, {
  ServiceLevelCategoryByToken,
  fromJSONServiceLevelTerms,
  fromWebRatesServiceLevel,
  isOneRate,
  serviceLevelMatchesToken,
  type JSONServiceLevel,
  type WebRatesServiceLevel,
} from '~/types/ServiceLevel';

enum RateAttribute {
  BEST_OPTION = 'BEST_OPTION',
  BESTVALUE = 'BESTVALUE',
  CHEAPEST = 'CHEAPEST',
  FASTEST = 'FASTEST',
  POPULAR = 'POPULAR',
}

export type ReturnRate = {
  amount: number;
};

export const rateDisplayAttribute = {
  BEST_OPTION: 'BEST_OPTION',
  CHEAPEST: 'CHEAPEST',
  CHEAPEST_AND_FASTEST: 'CHEAPEST_AND_FASTEST',
  FASTEST: 'FASTEST',
  MORE: 'MORE',
  PAY_ON_USE: 'PAY_ON_USE',
  POPULAR: 'POPULAR',
} as const;

export type RateDisplayAttribute = (typeof rateDisplayAttribute)[keyof typeof rateDisplayAttribute];

const serviceLevelToken = {
  FEDEX_GROUND_ECONOMY: 'fedex_ground_economy',
  UPS_GROUND: 'ups_ground',
  UPS_GROUND_SAVER: 'ups_ground_saver',
  USPS_FIRST_CLASS: 'usps_first_class',
  USPS_GROUND_ADVANTAGE: 'usps_ground_advantage',
  USPS_PARCEL_SELECT: 'usps_parcel_select',
  USPS_PRIORITY: 'usps_priority',
  USPS_PRIORITY_EXPRESS: 'usps_priority_express',
} as const;

export type RateObjectId = string;

type BaseRate = {
  amount: string;
  currency: string;
  durationTerms: string;
  objectId: RateObjectId;
  objectPurpose?: string;
  objectState?: string;
  provider: string;
  providerImage75: string;
  providerImage200: string;
  shipment: string;
};

type JSONBaseRate = {
  amount: string;
  currency: string;
  duration_terms: string;
  object_id: string;
  object_purpose: string;
  object_state: string;
  provider: string;
  provider_image_75: string;
  provider_image_200: string;
  shipment: string;
};

const fromJSONBaseRate = (jsonBaseRate: JSONBaseRate): BaseRate => ({
  amount: jsonBaseRate.amount,
  currency: jsonBaseRate.currency,
  durationTerms: jsonBaseRate.duration_terms,
  objectId: jsonBaseRate.object_id,
  objectPurpose: jsonBaseRate.object_purpose,
  objectState: jsonBaseRate.object_state,
  provider: jsonBaseRate.provider,
  providerImage75: jsonBaseRate.provider_image_75,
  providerImage200: jsonBaseRate.provider_image_200,
  shipment: jsonBaseRate.shipment,
});

type JSONRate = {
  amount: string;
  amount_local: string;
  arrives_by: string;
  attributes: string[];
  carrier_account: string;
  currency: string;
  currency_local: string;
  duration_terms: string;
  estimated_days: number;
  included_insurance_price?: string;
  insurance: boolean;
  messages: string[];
  object_created: string;
  object_id: string;
  object_owner: string;
  provider: string;
  provider_image_75: string;
  provider_image_200: string;
  servicelevel: JSONServiceLevel;
  servicelevel_name?: string;
  servicelevel_terms?: string;
  servicelevel_token?: string;
  shipment: string;
  test: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  zone: any;
};

interface Rate extends BaseRate {
  amountLocal: string;
  arrivesBy: string;
  attributes: RateAttribute[];
  carrierAccount: string;
  currencyLocal: string;
  durationTerms: string;
  estimatedDays: number;
  estimatedDeliveryDate?: string;
  includedInsurancePrice?: string;
  insurance: boolean;
  messages: string[];
  objectCreated: moment.Moment;
  objectOwner: string;
  retailAmount?: number;
  servicelevel: ServiceLevel;
  servicelevelName?: string;
  test: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  zone: any;
}

export type WebRate = {
  amount: string;
  amount_local: string;
  arrives_by: string;
  attributes: string[];
  carrier_account: string;
  currency: keyof typeof currencies;
  currency_local: keyof typeof currencies;
  duration_terms: string;
  estimated_days: number;
  included_insurance_price: string;
  messages: string[];
  object_created: Date;
  object_id: string;
  object_owner: string;
  provider: string;
  provider_image_75: string;
  provider_image_200: string;
  servicelevel: WebRatesServiceLevel;
  shipment: string;
  test: boolean;
  zone: string;
};

type RateLabel = {
  provider: string;
  servicelevel: ServiceLevel;
};

const fromJSON = (jsonRate: JSONRate): Rate => {
  // transactions.rates and shipments.rates have different response shape for servicelevel
  // this handles shipments.rates.servicelevel vs transactions.rates.servicelevel_*
  let jsonRateServiceLevel = jsonRate.servicelevel;
  if (!jsonRate.servicelevel) {
    jsonRateServiceLevel = {
      name: jsonRate.servicelevel_name || '',
      terms: jsonRate.servicelevel_terms || '',
      token: jsonRate.servicelevel_token || '',
    };
  }
  return {
    amount: jsonRate.amount,
    amountLocal: jsonRate.amount_local,
    arrivesBy: jsonRate.arrives_by,
    attributes: jsonRate.attributes.map((attr) => <RateAttribute>attr),
    carrierAccount: jsonRate.carrier_account,
    currency: jsonRate.currency,
    currencyLocal: jsonRate.currency_local,
    durationTerms: jsonRate.duration_terms,
    estimatedDays: jsonRate.estimated_days,
    includedInsurancePrice: jsonRate.included_insurance_price,
    insurance: jsonRate.insurance,
    messages: jsonRate.messages,
    objectCreated: moment(jsonRate.object_created),
    objectId: jsonRate.object_id,
    objectOwner: jsonRate.object_owner,
    provider: jsonRate.provider,
    providerImage75: jsonRate.provider_image_75,
    providerImage200: jsonRate.provider_image_200,
    servicelevel: {
      ...jsonRateServiceLevel,
      terms: fromJSONServiceLevelTerms(jsonRateServiceLevel.terms, jsonRate.provider),
    },
    servicelevelName: jsonRate.servicelevel_name || '',
    shipment: jsonRate.shipment,
    test: jsonRate.test,
    zone: jsonRate.zone,
  };
};

export const fromWebRate = (webRate: WebRate): Rate => {
  const attributes = webRate.attributes.map((t) => <RateAttribute>t);
  const servicelevel = fromWebRatesServiceLevel(webRate.servicelevel);
  return {
    amount: webRate.amount,
    amountLocal: webRate.amount_local,
    arrivesBy: webRate.arrives_by,
    attributes,
    carrierAccount: webRate.carrier_account,
    currency: webRate.currency,
    currencyLocal: webRate.currency_local,
    durationTerms: webRate.duration_terms,
    estimatedDays: webRate.estimated_days,
    includedInsurancePrice: webRate.included_insurance_price,
    // Not sure where this value would come from or when it would be true
    insurance: false,
    messages: webRate.messages,
    objectCreated: moment(webRate.object_created),
    objectId: webRate.object_id,
    objectOwner: webRate.object_owner,
    objectPurpose: undefined,
    //nulls,
    objectState: undefined,
    provider: webRate.provider,
    providerImage75: webRate.provider_image_75,
    providerImage200: webRate.provider_image_200,

    servicelevel,
    servicelevelName: webRate.servicelevel?.name,

    shipment: webRate.shipment,
    test: webRate.test,

    zone: webRate.zone,
  };
};

const getLabel = (rate: RateLabel): string => {
  // Edge cases where label does not follow "<provider> <service level name>" format
  // See tech debt ticket: https://shippo.atlassian.net/browse/ENG-13200
  // Ticket proposes updating /shipments endpoint to return rates with a servicelevel label field
  // so frontend does not need to know how to compile the service level label
  const serviceLevelNamesToCheck = ['Ground', 'Ground Saver'];
  if (rate.provider === 'UPS' && serviceLevelNamesToCheck.includes(rate.servicelevel.name)) {
    // UPS Ground needs to look like UPS® Ground
    return `${rate.provider}® ${rate.servicelevel.name}`;
  }

  // https://shippo.atlassian.net/browse/ENG-29904
  if (carrierRebrandMap[rate.provider])
    return `${carrierRebrandMap[rate.provider]} ${rate.servicelevel.name}`;

  return `${rate.provider} ${rate.servicelevel.name}`;
};

const getLabelWithOneRate = (rate: RateLabel): string => {
  const { servicelevel } = rate;
  const oneRateText = isOneRate(servicelevel) ? ' One Rate' : '';
  return `${getLabel(rate)}${oneRateText}`;
};

export const addAttributesToRateMap = (attributes: RateAttribute[]) => (rate: Rate) => ({
  ...rate,
  attributes,
});

const popularServicesV2 = [
  serviceLevelToken.USPS_PRIORITY,
  serviceLevelToken.UPS_GROUND,
  serviceLevelToken.UPS_GROUND_SAVER,
];

const getBestRates = (rates: Rate[]) =>
  rates.filter((r: Rate) => r.attributes?.includes(RateAttribute.BEST_OPTION));

/**
 * This function adds popular attribute to rate based on the new logic
 * https://shippo.atlassian.net/browse/ENG-41605
 * Show these services as popular, sorted by cheapest, removing the cheapest if it is among these services to avoid duplication in the cheapest tag:
 * USPS Priority Mail
 * FedEx Ground Economy
 * UPS Ground
 * UPS Ground Saver
 *
 * TODO: Move this logic to the backend
 * @param rates the list of rates for a shipment
 * @returns the same list of rates but with one rate potentially attributed as POPULAR based on business logic
 */
export const attributePopularRateV2 = (rates: Rate[]): Rate[] => {
  // Find the cheapest rates
  const cheapestRates = rates.filter((r: Rate) => r.attributes.includes(RateAttribute.CHEAPEST));
  const bestOption = getBestRates(rates);

  // Find all the other rates than cheapest rates
  const otherThanCheapestAndBestRates = differenceBy(
    rates,
    [...cheapestRates, ...bestOption],
    (r: Rate) => r.objectId,
  );

  // Find all the popular rates and sort them by price
  const popularRates = otherThanCheapestAndBestRates
    .filter((r: Rate) =>
      popularServicesV2.includes(r.servicelevel.token as (typeof popularServicesV2)[number]),
    )
    .sort((a, b) => sortBy(a, b, 'cheapest'));

  // Get all the other rates than popular rates and cheapest rates
  const otherRates = differenceBy(
    rates,
    cheapestRates.concat(popularRates),
    (r: Rate) => r.objectId,
  );

  // Adding POPULAR to attribute of each popular rate for tracking
  const updatedPopularRatesList = popularRates.map(addAttributesToRateMap([RateAttribute.POPULAR]));

  return uniqBy(
    updatedPopularRatesList.concat(cheapestRates).concat(otherRates),
    (rate: Rate) => rate.objectId,
  );
};

const getCheapestRates = (rates: Rate[]) => {
  // Filter out rates with invalid or zero amounts
  const numericRates = rates.filter((rate) => parseFloat(rate.amount));
  if (numericRates.length === 0) return [];

  const sortedRates = [...numericRates].sort((a, b) => sortBy(a, b, 'cheapest'));
  const cheapestAmount = parseFloat(sortedRates[0].amount);

  return sortedRates.filter((rate) => parseFloat(rate.amount) === cheapestAmount);
};

const getFastestRate = (rates: Rate[]) => {
  // Filter only rates that have a numeric estimatedDays value, includes 0
  const ratesWithEstimates = rates.filter(
    (rate): rate is { estimatedDays: number } & Rate => typeof rate.estimatedDays === 'number',
  );

  if (ratesWithEstimates.length === 0) return [];

  const sortedByFastest = [...ratesWithEstimates].sort((a, b) => sortBy(a, b, 'fastest'));
  const fastestEstimate = sortedByFastest[0].estimatedDays;
  const fastestRates = sortedByFastest.filter((rate) => rate.estimatedDays === fastestEstimate);
  // Show only 1 "fastest" rate
  const cheapestAndFastestRate = [...fastestRates].sort((a, b) => sortBy(a, b, 'cheapest'))[0];

  return cheapestAndFastestRate ? [cheapestAndFastestRate] : [];
};

const getRatesByAttribute = (rates: Rate[]) => {
  const bestRates = getBestRates(rates);
  // Remove best rates from cheapest and fastest lists
  const cheapestRates = differenceBy(getCheapestRates(rates), bestRates, (r: Rate) => r.objectId);
  const fastestRates = differenceBy(getFastestRate(rates), bestRates, (r: Rate) => r.objectId);
  const popularRates = differenceBy(
    rates.filter((r: Rate) => r.attributes?.includes(RateAttribute.POPULAR)),
    [...cheapestRates, ...fastestRates, ...bestRates],
    (r: Rate) => r.objectId,
  );

  // Note that "Best Value" rates aren't currently being shown separately; they're just being
  // included in "other rates"

  const otherRates = differenceBy(
    rates,
    [...cheapestRates, ...fastestRates, ...popularRates, ...bestRates],
    (r: Rate) => r.objectId,
  ).sort((a, b) => sortBy(a, b, 'cheapest'));

  const cheapestAndFastest: Rate[] = intersectionBy(
    cheapestRates,
    fastestRates,
    (r: Rate) => r.objectId,
  );

  const cheapestButNotFastest = differenceBy(
    cheapestRates,
    cheapestAndFastest,
    (r: Rate) => r.objectId,
  );

  const fastestButNotCheapest = differenceBy(
    fastestRates,
    cheapestAndFastest,
    (r: Rate) => r.objectId,
  ).sort((a, b) => sortBy(a, b, 'cheapest'));

  return {
    bestOption: bestRates,
    cheapestAndFastest,
    cheapestRates: cheapestButNotFastest,
    fastestRates: fastestButNotCheapest,
    otherRates,
    popularRates,
  };
};

/**
 * Returns an array of rates grouped by display attribute, in the order they should be displayed.
 * If the rates are for a return shipment, all rates are displayed as "Pay on use".
 *
 * Order: Best (if available), Cheapest, Fastest, Cheapest & Fastest, Popular, More
 *
 * @param rates the list of rates for a shipment
 * @param isReturn whether the shipment is a return
 */
const getRatesWithAttributeInDisplayOrder = (
  rates: Rate[],
  isReturn = false,
): {
  displayAttribute: RateDisplayAttribute;
  rates: Rate[];
}[] => {
  if (isReturn) {
    const ratesFromCheapestToMostExpensive = orderBy(rates, (rate) => parseFloat(rate.amount));
    return [
      {
        displayAttribute: rateDisplayAttribute.PAY_ON_USE,
        rates: ratesFromCheapestToMostExpensive,
      },
    ];
  }

  const rateCategories = getRatesByAttribute(rates);

  /**
   * Hide the BEST label when a separate Cheapest + Fastest rate exists.
   * When a rate is Best + Cheapest + Fastest it stays like that.
   *
   * Demoted Best rates are moved into the Popular group so the row still
   * renders without the "Best" label.
   */
  const shouldDemoteBest =
    rateCategories.cheapestAndFastest.length > 0 && rateCategories.bestOption.length > 0;
  const bestOption = shouldDemoteBest ? [] : rateCategories.bestOption;
  const popularRates = shouldDemoteBest
    ? uniqBy(
        [...rateCategories.popularRates, ...rateCategories.bestOption],
        (r: Rate) => r.objectId,
      )
    : rateCategories.popularRates;

  const rateGroupsByAttribute: Record<string, Rate[]> = {
    [rateDisplayAttribute.BEST_OPTION]: bestOption,
    [rateDisplayAttribute.CHEAPEST]: rateCategories.cheapestRates,
    [rateDisplayAttribute.CHEAPEST_AND_FASTEST]: rateCategories.cheapestAndFastest,
    [rateDisplayAttribute.FASTEST]: rateCategories.fastestRates,
    [rateDisplayAttribute.MORE]: rateCategories.otherRates,
    [rateDisplayAttribute.POPULAR]: popularRates,
  };

  // Order: Best, Cheapest, Fastest, Cheapest & Fastest, Popular, More
  const displayOrder: RateDisplayAttribute[] = [
    rateDisplayAttribute.BEST_OPTION,
    rateDisplayAttribute.CHEAPEST,
    rateDisplayAttribute.FASTEST,
    rateDisplayAttribute.CHEAPEST_AND_FASTEST,
    rateDisplayAttribute.POPULAR,
    rateDisplayAttribute.MORE,
  ];

  return displayOrder
    .map((displayAttribute) => ({
      displayAttribute,
      rates: rateGroupsByAttribute[displayAttribute],
    }))
    .filter((group) => group.rates.length > 0);
};

/**
 * Computes a map of rate object IDs to the multiple category display attributes they qualify for
 * Unlike `getRatesByAttribute` which places each rate into exactly one mutually-exclusive group,
 * this function allows a single rate to carry multiple categories simultaneously
 * (eg. a rate can be both "Cheapest" and "Fastest")
 *
 * Only considers CHEAPEST, FASTEST, and BEST_OPTION categories
 * Currently only intended for use with the new defaults experience feature flag
 *
 * @param rates the list of rates for a shipment
 * @returns a map of rate objectId to an array of RateDisplayAttribute values
 */
const getRateCategoryAttributes = (rates: Rate[]): Record<RateObjectId, RateDisplayAttribute[]> => {
  const bestRates = getBestRates(rates);
  const cheapestRates = getCheapestRates(rates);
  const fastestRates = getFastestRate(rates);

  /**
   * Hide BEST label when a Cheapest + Fastest exists,
   * except for when a rate is Best, Cheapest, and Fastest.
   */
  const cheapestAndFastestRate = intersectionBy(
    cheapestRates,
    fastestRates,
    (r: Rate) => r.objectId,
  );
  const bestCheapestAndFastestRate = intersectionBy(
    bestRates,
    cheapestAndFastestRate,
    (r: Rate) => r.objectId,
  );
  const calculatedBestRates =
    cheapestAndFastestRate.length > 0 ? bestCheapestAndFastestRate : bestRates;

  const categoryMap: Record<RateObjectId, RateDisplayAttribute[]> = {};

  for (const rate of calculatedBestRates) {
    categoryMap[rate.objectId] = [
      ...(categoryMap[rate.objectId] ?? []),
      rateDisplayAttribute.BEST_OPTION,
    ];
  }

  for (const rate of cheapestRates) {
    categoryMap[rate.objectId] = [
      ...(categoryMap[rate.objectId] ?? []),
      rateDisplayAttribute.CHEAPEST,
    ];
  }

  for (const rate of fastestRates) {
    categoryMap[rate.objectId] = [
      ...(categoryMap[rate.objectId] ?? []),
      rateDisplayAttribute.FASTEST,
    ];
  }

  return categoryMap;
};

const getRatesForServiceLevelToken = (
  rates: Rate[],
  serviceLevelToken: string,
  preferredAccountObjectID?: string,
): Rate[] => {
  // if there are no rates provided, don't bother searching them
  if (rates.length < 1) return rates;

  // check if the service level token is a category, such as "CHEAPEST" - and search for Rates with that property
  const serviceLevelCategory = ServiceLevelCategoryByToken[serviceLevelToken]?.token?.toUpperCase();
  if (serviceLevelCategory) {
    const { bestOption, cheapestRates, fastestRates } = getRatesByAttribute(rates);
    if (serviceLevelCategory === 'CHEAPEST' && cheapestRates.length > 0) return cheapestRates;
    if (serviceLevelCategory === 'FASTEST' && fastestRates.length > 0) return fastestRates;
    if (serviceLevelCategory === 'BEST' && bestOption.length > 0) return bestOption;
  }

  // otherwise, check if the rates have matching service level tokens
  return rates.filter((rate) => {
    // we can disqualify this rate if a Preferred Carrier Account was specified and it doesn't match the Rate's Carrier
    if (preferredAccountObjectID && rate.carrierAccount !== preferredAccountObjectID) return false;

    return serviceLevelMatchesToken(rate.servicelevel, serviceLevelToken);
  });
};

interface RateTotal {
  amount: number;
  currency: string;
  includedInsurancePrice?: number;
  numberWithInsurance?: number;
  numberWithSignature?: number;
  rate: { provider: string; servicelevel: ServiceLevel };
  returnAmount?: number;
  shipmentObjectId?: string;
  signatureConfirmation?: {
    hasSignatureConfirmation: boolean;
    signatureConfirmationType:
      | 'ADULT'
      | 'CARRIER_CONFIRMATION'
      | 'CERTIFIED'
      | 'INDIRECT'
      | 'STANDARD';
  };
  total: number;
  totalInsuranceAmount: number;
}

const createRateList = (
  selectedRatesForValidOrders: string[],
  ratesData: HashMap<string, Rate>,
  validBatchEntries?: BatchEntryExtended[],
): RateTotal[] => {
  const selectedRates = selectedRatesForValidOrders.map((selectedRate) => ratesData[selectedRate!]);

  // We need to group rates to show count of each type and how much is the cost
  return selectedRates.reduce((list: RateTotal[], selectedRate) => {
    // First we search to see if the rate is already in our list that we are creating
    if (!selectedRate) return list;

    const rateIndex = list.findIndex(
      ({ rate }: RateTotal) =>
        rate.provider === selectedRate.provider &&
        rate.servicelevel.token === selectedRate.servicelevel.token,
    );

    // this is a temporary solution to pull insurance for rate selection that avoids an API change
    // the Rate object has an amount_insurance_fee field that can be used here
    // todo https://shippo.atlassian.net/browse/ENG-25100
    const batchEntry = validBatchEntries?.find(
      ({ shipmentObjectId }) => selectedRate.shipment === shipmentObjectId,
    );

    const insuranceAmount = batchEntry?.extras?.insurance?.amount || 0;
    const isReturnRequested = batchEntry?.extras?.isReturnRequested;

    const signatureConfirmation = batchEntry?.extras?.signatureConfirmation;

    // If we found it, we are going to update it adding up to the amount and the total price
    if (rateIndex !== -1) {
      return [
        ...list.slice(0, rateIndex),
        {
          ...list[rateIndex],
          amount: list[rateIndex].amount + 1,
          currency: selectedRate.currency,
          includedInsurancePrice:
            Number(list[rateIndex].includedInsurancePrice || null) +
            Number(selectedRate?.includedInsurancePrice || null),
          numberWithInsurance:
            Number(insuranceAmount) > 0
              ? (list[rateIndex].numberWithInsurance || 0) + 1
              : list[rateIndex].numberWithInsurance || 0,
          numberWithSignature: signatureConfirmation?.hasSignatureConfirmation
            ? (list[rateIndex].numberWithSignature || 0) + 1
            : list[rateIndex].numberWithSignature || 0,
          returnAmount:
            list[rateIndex].returnAmount &&
            (list[rateIndex].returnAmount as number) + (isReturnRequested ? 1 : 0),
          signatureConfirmation,
          total: list[rateIndex].total + Number(selectedRate.amount),
          totalInsuranceAmount: list[rateIndex].totalInsuranceAmount + Number(insuranceAmount),
        },
        ...list.slice(rateIndex + 1),
      ] as RateTotal[];
    }

    // If we didn't found any we will add it to our list with the amount and total for just that rate
    return [
      ...list,
      {
        amount: 1,
        currency: selectedRate.currency,
        includedInsurancePrice: Number(selectedRate?.includedInsurancePrice) || 0,
        numberWithInsurance: Number(insuranceAmount) > 0 ? 1 : 0,
        numberWithSignature: signatureConfirmation?.hasSignatureConfirmation ? 1 : 0,
        rate: { provider: selectedRate.provider, servicelevel: selectedRate.servicelevel },
        returnAmount: isReturnRequested ? 1 : 0,
        shipmentObjectId: selectedRate.shipment,
        signatureConfirmation,
        total: Number(selectedRate.amount),
        totalInsuranceAmount: Number(insuranceAmount) || 0,
      },
    ] as RateTotal[];
  }, []);
};

const getRateListTotalText = (rateList: (RateTotal | ReturnRate)[]): string => {
  const rates = rateList.filter((rate) => rate.hasOwnProperty('rate')) as RateTotal[];
  const ratesTotal = rates.reduce((total, label) => total + label.total, 0);
  const currencyIso = rates[0] ? rates[0].currency : currencies.USD.iso;
  const noValue = `${currencies[currencyIso].symbol}—`;

  return ratesTotal
    ? i18n.t('translations:currency', {
        value: {
          currency_iso: currencyIso,
          value: ratesTotal,
        },
      })
    : noValue;
};

const getRateListTotalTextNoInsurance = (rateList: (RateTotal | ReturnRate)[]): string => {
  const rates = rateList.filter((rate) => rate.hasOwnProperty('rate')) as RateTotal[];
  const ratesTotal = rates.reduce(
    (total, label) => total + (label.total - Number(label.includedInsurancePrice)),
    0,
  );
  const currencyIso = rates[0] ? rates[0].currency : currencies.USD.iso;
  const noValue = `${currencies[currencyIso].symbol}—`;

  return ratesTotal
    ? i18n.t('translations:currency', {
        value: {
          currency_iso: currencyIso,
          value: ratesTotal,
        },
      })
    : noValue;
};

type RateSortOption = 'cheapest' | 'expensive' | 'fastest' | 'slowest';
// API Usage:
// rates.sort((a, b) => sortBy(a, b, 'cheapest'));
const sortBy = (a: Rate, b: Rate, option: RateSortOption) => {
  switch (option) {
    case 'cheapest':
      return parseFloat(a.amount) - parseFloat(b.amount);
    case 'expensive':
      return parseFloat(b.amount) - parseFloat(a.amount);
    case 'fastest':
      return a.estimatedDays - b.estimatedDays;
    case 'slowest':
      return b.estimatedDays - a.estimatedDays;
    default:
      return 0;
  }
};

const untrackableServiceLevels = [
  'First Class Package International Service',
  'CanadaPost Small Packets',
];

const isUntrackableRateServiceLevel = (rate: Rate): boolean =>
  !!rate.servicelevel.name && untrackableServiceLevels.includes(rate.servicelevel.name);

type JSONRateTransactionSearch = {
  // string representation of a number
  amount: string;
  // string representation of a number
  amount_insurance_fee: string;
  // string representation of a number
  amount_local: string;
  // a UUID
  carrier_account: string;
  currency: keyof typeof currencies;
  currency_local: keyof typeof currencies;
  // TODO: unknown
  discount_group_name?: null;
  // string representation of a number
  insurance_amount: string;
  insurance_currency: keyof typeof currencies | null;
  object_id: string;
  provider: string;
  servicelevel_name: string;
  servicelevel_token: string;
};

type RateTransactionSearch = {
  // string representation of a number
  amount: string;
  // string representation of a number
  amountInsuranceFee: string;
  // string representation of a number
  amountLocal: string;
  // a UUID
  carrierAccount: string;
  currency: keyof typeof currencies;
  currencyLocal: keyof typeof currencies;
  // TODO: unknown
  discountGroupName?: null;
  // string representation of a number
  insuranceAmount: string;
  insuranceCurrency: keyof typeof currencies | null;
  objectId: string;
  provider: string;
  servicelevelName: string;
  servicelevelToken: string;
};

const transactionSearchRatefromJSON = (
  jsonRate: JSONRateTransactionSearch,
): RateTransactionSearch => ({
  // string representation of a number
  amount: jsonRate.amount,
  // string representation of a number
  amountInsuranceFee: jsonRate.amount_insurance_fee,
  // string representation of a number
  amountLocal: jsonRate.amount_local,
  // a UUID
  carrierAccount: jsonRate.carrier_account,
  currency: jsonRate.currency,
  currencyLocal: jsonRate.currency_local,
  // TODO: unknown
  discountGroupName: jsonRate.discount_group_name,
  // string representation of a number
  insuranceAmount: jsonRate.insurance_amount,
  insuranceCurrency: jsonRate.insurance_currency || null,
  objectId: jsonRate.object_id,
  provider: jsonRate.provider,
  servicelevelName: jsonRate.servicelevel_name,
  servicelevelToken: jsonRate.servicelevel_token,
});

const transactionSearchRateFromWeb = (webRate: WebRate): RateTransactionSearch => ({
  amount: webRate.amount,
  amountInsuranceFee: '',
  amountLocal: webRate.amount_local,
  carrierAccount: webRate.carrier_account,
  currency: webRate.currency,
  currencyLocal: webRate.currency_local,
  insuranceAmount: '',
  insuranceCurrency: null,
  objectId: webRate.object_id,
  provider: webRate.provider,
  servicelevelName: webRate.servicelevel.name,
  servicelevelToken: webRate.servicelevel.token,
});

export default Rate;
export {
  BaseRate,
  JSONBaseRate,
  JSONRate,
  JSONRateTransactionSearch,
  RateAttribute,
  RateTotal,
  RateTransactionSearch,
  ServiceLevel,
  createRateList,
  fromJSON,
  fromJSONBaseRate,
  getLabelWithOneRate,
  getRateCategoryAttributes,
  getRateListTotalText,
  getRateListTotalTextNoInsurance,
  getRatesByAttribute,
  getRatesForServiceLevelToken,
  getRatesWithAttributeInDisplayOrder,
  isUntrackableRateServiceLevel,
  transactionSearchRateFromWeb,
  transactionSearchRatefromJSON,
};
